import { fetchPostByCustomerAndPostId } from './postService';
import {
  TIKTOK_PROCESSING_MESSAGE,
  evaluateTikTokPublishState,
  isTikTokPlatform,
} from "../features/tiktok/utils/tiktokOptions";
// Wrapper: publish post, then fetch platform_post_id and business_page_id


import axios from 'axios';
import { Platform } from '../types/Platforms';
import { debug, formatSafeErrorMessage } from "../utils/debugLogger";
import { normalizeScheduledTime } from "../utils/scheduleTime";
import { normalizePublicMediaUrl } from "../utils/mediaUrl";
import { applyInstagramCarouselToPublishPayload } from "../utils/applyInstagramCarouselToPublishPayload";
import {
  isFacebookReelsPlatform,
  isFacebookReelsPublishPending,
} from "../utils/facebookReelsUtils";
import type {
  GSCExchangeResponse,
  GSCSavePropertyResponse,
} from "../features/google/platforms/gsc/types/gsc.types";
import type {
  BingExchangeResponse,
  BingSaveSiteResponse,
} from "../features/bing/types/bing.types";

// Publish a post to social media
export const publishSocialPost = async (
  postPayload: any, // You can define a more specific type if needed
  idToken: string
): Promise<any> => {
  try {
    // Expect postPayload to have customer_id
    const customerId = postPayload.customer_id;
    if (!customerId) throw new Error('customer_id is required in postPayload');
    const payload: Record<string, unknown> = {
      ...postPayload,
      image_url: normalizePublicMediaUrl(postPayload.image_url ?? ""),
      video_url: normalizePublicMediaUrl(postPayload.video_url ?? ""),
    };
    applyInstagramCarouselToPublishPayload(payload);
    const response = await axios.post(
      `${SOCIAL_API_BASE}/${customerId}/social/posts`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      }
    );
    debug.log("Social post published", { ok: true });
    return response.data;
  } catch (error: unknown) {
    debug.error("Error publishing social post:", formatSafeErrorMessage(error));
    throw error;
  }
};

/**
 * Normalize scheduler datetime to UTC ISO expected by backend services.
 * Accepts both `datetime-local` values and ISO strings.
 *
 * The implementation now lives in utils/scheduleTime so blogs and social posts
 * share one definition. Re-exported here so existing importers are unaffected.
 * (Imported as well as re-exported: `export ... from` alone would not give this
 * module a local binding, and buildScheduledPostUpdates below uses it.)
 */
export { normalizeScheduledTime };

/**
 * Shared scheduler-ready update payload for posts persisted in content service.
 * The scheduler reads post state from storage, so keep status/publish_type aligned.
 *
 * `timezone` is the IANA zone the user picked in. It is optional so existing
 * callers keep compiling and keep their browser-local behaviour; pass it and the
 * wall-clock resolves against that zone instead, and the zone id is stored
 * alongside the instant so every later view can render the clock that was meant.
 */
export const buildScheduledPostUpdates = (
  scheduledTime: string,
  timezone?: string
) => ({
  scheduled_time: normalizeScheduledTime(scheduledTime, timezone),
  scheduled_timezone: timezone || '',
  publish_type: 'scheduled',
  status: 'APPROVED',
});

/**
 * Planned-time payload for a PENDING post (SGL-501). Deliberately has NO `status`:
 * the post stays PENDING, and the scheduler (which only scans APPROVED rows)
 * ignores it until the owner approves, at which point the stored time becomes
 * the real schedule.
 */
export const buildPlannedPostUpdates = (
  scheduledTime: string,
  timezone?: string
) => ({
  scheduled_time: normalizeScheduledTime(scheduledTime, timezone),
  scheduled_timezone: timezone || '',
  publish_type: 'scheduled',
});

/** Align with Post Manager approve: Social pipeline expects APPROVED in content DB before publish. */
export const buildPublishNowRecordUpdates = () => ({
  status: 'APPROVED',
});

export const getApiErrorMessage = (
  error: any,
  fallback = 'Request failed. Please try again.'
): string => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
};

/** User-facing detail when axios gets no response (CORS, extension blockers, offline, etc.). */
export const describeAxiosOrPublishError = (
  error: unknown,
  fallback = 'Request failed. Please try again.'
): string => {
  const anyErr = error as any;
  const base = getApiErrorMessage(anyErr, fallback);
  if (
    base === 'Network Error' ||
    anyErr?.code === 'ERR_NETWORK' ||
    anyErr?.code === 'ECONNABORTED'
  ) {
    return `${base} — the browser did not get a response. Common causes: an ad/privacy blocker blocking \`social/posts\`, offline/VPN, or a corporate firewall. Try a private window with extensions off, or inspect the failing request in DevTools (Network).`;
  }
  return base;
};

// Define interfaces
interface ProfileData {
  id: string;
  token: string;
  metadata: Record<string, any>;
}

interface TokenPayload {
  customer_id: string;
  created_by: string;
  platform: Platform;
  profiles: ProfileData[];
}

// Use social management API URL
const SOCIAL_API_BASE = process.env.REACT_APP_SOCIAL_API_URL || 'https://bc631mqtg1.execute-api.us-east-1.amazonaws.com/Prod';

debug.log("Social API env:", process.env.REACT_APP_ENV ?? "(unset)");

function isInstagramPayload(payload: any): boolean {
  return String(payload?.platform ?? "").toUpperCase().includes("INSTAGRAM");
}

/**
 * Instagram often persists ERROR + container id while media is still processing; the post can
 * succeed shortly after. Applies to Reels, video, carousel, and sometimes image flows.
 * Backend may store either structured JSON or `error: "Failed to post to Instagram: {'status_code': ...}"`
 * (Python-style quotes — regex must allow `'id': '123'` not only `id: 123`).
 */
function isRetryableInstagramTransientPublishState(
  platformResponse: any,
  payload: any
): boolean {
  if (!isInstagramPayload(payload)) return false;

  if (typeof platformResponse === "string") {
    const blob = platformResponse;
    return (
      /Instagram API Error/i.test(blob) &&
      /status_code/i.test(blob) &&
      /\d{10,}/.test(blob)
    );
  }

  if (
    platformResponse &&
    typeof platformResponse === "object" &&
    (platformResponse as { error?: unknown }).error &&
    typeof (platformResponse as { error?: unknown }).error === "object" &&
    !Array.isArray((platformResponse as { error?: unknown }).error)
  ) {
    if (
      isRetryableInstagramTransientPublishState(
        (platformResponse as { error: object }).error,
        payload
      )
    ) {
      return true;
    }
  }

  const pr =
    platformResponse && typeof platformResponse === "object"
      ? platformResponse
      : {};

  const statusCode = String(pr.status_code ?? "").toUpperCase();
  const errorType = String(pr.error_type ?? "").toUpperCase();
  const hasContainerId = Boolean(String(pr.id ?? "").trim());

  if (
    statusCode === "ERROR" &&
    hasContainerId &&
    errorType.includes("INSTAGRAM")
  ) {
    return true;
  }

  const errStr =
    typeof pr.error === "string"
      ? pr.error
      : typeof pr.message === "string"
        ? pr.message
        : "";
  if (!errStr) return false;

  // `Failed to post to Instagram: {'status_code': 'ERROR', 'id': '17…', 'error_type': 'Instagram API Error', …}`
  // Single quotes around keys/values break naive `id:` regexes — match the blob signature instead.
  return (
    /Instagram API Error/i.test(errStr) &&
    /status_code/i.test(errStr) &&
    /\d{10,}/.test(errStr)
  );
}

/** FB Reels async publish often surfaces string errors before `platform_post_id` is ready. */
function isRetryableFacebookReelsPublishError(
  platformResponse: any,
  payload: any
): boolean {
  if (!isFacebookReelsPlatform(payload?.platform)) return false;
  const err = platformResponse?.error;
  const msg =
    typeof err === "string"
      ? err
      : err && typeof err === "object"
        ? JSON.stringify(err)
        : String(err ?? "");
  const lower = msg.toLowerCase();
  if (!lower.trim()) return false;
  return (
    lower.includes("processing") ||
    lower.includes("not ready") ||
    lower.includes("try again") ||
    lower.includes("pending") ||
    lower.includes("still publishing") ||
    lower.includes("uploading")
  );
}

export const saveTokens = async (payload: TokenPayload, idToken: string): Promise<void> => {
  try {
    const response = await axios.post(
      `${SOCIAL_API_BASE}/${payload.customer_id}/tokens`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      }
    );
    
    debug.log('Social tokens saved successfully:', {
      customerId: payload.customer_id,
      platform: payload.platform,
      profileCount: payload.profiles.length
    });
    
    return response.data;
  } catch (error: unknown) {
    debug.error(
      "Error saving social tokens:",
      formatSafeErrorMessage(error),
      { customerId: payload.customer_id, platform: payload.platform }
    );
    throw error;
  }
};

/**
 * Disconnect a social platform connection: deletes the stored credentials for
 * one profile. `accountId` is required for GMB (its secret is keyed by
 * account + location, not the location id alone).
 */
export const deleteSocialToken = async (
  customerId: string,
  platform: Platform,
  profileId: string,
  idToken: string,
  accountId?: string
): Promise<void> => {
  const params = new URLSearchParams({ platform: String(platform), profile_id: profileId });
  if (accountId) params.set('account_id', accountId);
  const apiUrl = `${SOCIAL_API_BASE}/${customerId}/tokens?${params.toString()}`;
  try {
    await axios.delete(apiUrl, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    });
    debug.log('Social token disconnected', { customerId, platform, profileId });
  } catch (error: unknown) {
    debug.error("Error disconnecting social token:", formatSafeErrorMessage(error));
    throw new Error(getApiErrorMessage(error, 'Failed to disconnect'));
  }
};

export const getTokens = async (customerId: string, platform: Platform, idToken: string): Promise<ProfileData[]> => {
  try {
    const response = await axios.get(
      `${SOCIAL_API_BASE}/${customerId}/tokens?platform=${platform}`,
      {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      }
    );
    return response.data.profiles;
  } catch (error: unknown) {
    debug.error("Error fetching social tokens:", formatSafeErrorMessage(error));
    throw error;
  }
};
/**
 * Snapshot of every field a publish result writes to the post row.
 *
 * `POST /{customer_id}/social/posts` only enqueues to SQS and returns 200, so the
 * poll loop below starts before any publisher has touched the row. A post that
 * failed earlier still carries that attempt's `platform_response.error`, and one
 * whose media container was created still carries a `platform_post_id` — and
 * re-approving a failed post clears neither. Without this snapshot the poller
 * reads that leftover state on its first pass and reports the PREVIOUS attempt's
 * outcome as if it were this one's.
 *
 * Publishers write their result through EventBridge `PostPublished` →
 * content-management `handle_post_published_event`, which sets `status`,
 * `platform_response` and `updated_at` together. Comparing all of them rather
 * than `updated_at` alone keeps the guard working if a write path stops bumping
 * the timestamp, and still detects a fresh failure carrying the same error text,
 * since `status` moves off APPROVED either way.
 */
const publishStateFingerprint = (post: any): string =>
  JSON.stringify([
    post?.updated_at ?? "",
    post?.status ?? "",
    post?.platform_post_id ?? "",
    post?.platform_response ?? null,
  ]);

export const publishAndFetchPlatformPostId = async (
  postPayload: any,
  idToken: string,
  options?: { maxRetries?: number; delayMs?: number }
): Promise<{ 
  platform_post_id?: string; 
  business_page_id?: string; 
  platform_response?: any;
  error?: string;
  /** TikTok (SGL-170): terminal status when the row reached one (PUBLISHED / SENT_TO_TIKTOK). */
  status?: string;
  /** TikTok (SGL-170): still processing after the last poll. Not a failure. */
  pending?: boolean;
  message?: string;
}> => {
  const maxRetries = options?.maxRetries ?? 5;
  const delayMs = options?.delayMs ?? 2000;
  try {
    const postId = postPayload.post_id;
    const customerId = postPayload.customer_id;
    // Read the row *before* publishing so the loop can tell this attempt's result
    // from whatever the previous one left behind. Best effort — if the read fails
    // the guard is simply inert and the loop behaves as it always has.
    let prePublishFingerprint: string | undefined;
    try {
      const existing = await fetchPostByCustomerAndPostId(customerId, postId, idToken);
      const existingPost = Array.isArray(existing)
        ? existing.find(p => p.post_id === postId)
        : undefined;
      if (existingPost) {
        prePublishFingerprint = publishStateFingerprint(existingPost);
      }
    } catch {
      prePublishFingerprint = undefined;
    }
    // Publish the post
    await publishSocialPost(postPayload, idToken);
    let lastError: string | undefined;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const posts = await fetchPostByCustomerAndPostId(customerId, postId, idToken);
      const postDetails = Array.isArray(posts) ? posts.find(p => p.post_id === postId) : undefined;
      if (!postDetails) {
        lastError = 'Post details not found';
      } else if (
        prePublishFingerprint !== undefined &&
        publishStateFingerprint(postDetails) === prePublishFingerprint
      ) {
        // Nothing written since the publish was accepted, so every field below
        // still belongs to the previous attempt — keep waiting rather than read
        // it. Reuses the existing wording on purpose: usePostActions matches this
        // exact string to surface slow carousel/video/Reels publishes as pending.
        lastError = "platform_post_id not yet available";
      } else {
        const pr = postDetails.platform_response;

        if (isTikTokPlatform(postPayload.platform)) {
          // TikTok: the row status is authoritative. PUBLISHED may carry no
          // platform_post_id (SELF_ONLY), SENT_TO_TIKTOK is the inbox draft, and
          // anything else is TikTok still processing, which is not an error.
          const outcome = evaluateTikTokPublishState(postDetails);
          if (outcome.kind === "done") {
            return {
              status: outcome.status,
              platform_post_id: postDetails.platform_post_id || undefined,
              business_page_id: postDetails.business_profile_id,
              platform_response: postDetails.platform_response,
            };
          }
          if (outcome.kind === "failed") return { error: outcome.error };
          lastError = TIKTOK_PROCESSING_MESSAGE;
        } else if (postDetails.platform_post_id) {
          if (isFacebookReelsPlatform(postPayload.platform)) {
            if (isFacebookReelsPublishPending(pr)) {
              lastError = "Facebook is still processing the Reel.";
            } else {
              return {
                platform_post_id: postDetails.platform_post_id,
                business_page_id: postDetails.business_profile_id,
                platform_response: postDetails.platform_response,
              };
            }
          } else {
            return {
              platform_post_id: postDetails.platform_post_id,
              business_page_id: postDetails.business_profile_id,
              platform_response: postDetails.platform_response,
            };
          }
        } else if (isRetryableInstagramTransientPublishState(pr, postPayload)) {
          lastError =
            "Instagram is still processing your media. This may take a minute…";
        } else if (isRetryableFacebookReelsPublishError(pr, postPayload)) {
          lastError = "Facebook is still processing the Reel.";
        } else if (pr?.error) {
          return {
            error:
              typeof pr.error === "string"
                ? pr.error
                : JSON.stringify(pr.error),
          };
        } else {
          lastError = "platform_post_id not yet available";
        }
      }
      // Wait before retrying
      if (attempt < maxRetries - 1) {
        await new Promise(res => setTimeout(res, delayMs));
      }
    }
    // TikTok: the row never moved off its pre-publish state (the stale-row guard's
    // wording) or is still processing. Neither is a failure.
    if (
      isTikTokPlatform(postPayload.platform) &&
      (lastError === TIKTOK_PROCESSING_MESSAGE || lastError === "platform_post_id not yet available")
    ) {
      return { pending: true, message: TIKTOK_PROCESSING_MESSAGE };
    }
    return { error: lastError ?? 'Unknown error' };
  } catch (err: any) {
    return { error: err.message || 'Unknown error' };
  }
};

// LinkedIn token exchange interfaces
export interface LinkedInOrganizationAcl {
  organization: string; // e.g. "urn:li:organization:XYZ789"
  role: string;         // e.g. "ADMINISTRATOR"
  "organization~"?: {
    localizedName?: string;
    vanityName?: string;
    logoV2?: { "original~"?: { elements?: Array<{ identifiers?: Array<{ identifier?: string }> }> } };
  };
}

export interface LinkedInTokenResponse {
  success: boolean;
  scope: string;
  profile_id: string; // LinkedIn member ID (e.g., "urn:li:person:ABC123")
  profile: LinkedInProfile;
  organizations?: LinkedInOrganizationAcl[];
  message: string;
}

// Business (Community Management) exchange has no OIDC profile — orgs only.
export interface LinkedInBusinessTokenResponse {
  success: boolean;
  scope: string;
  organizations: LinkedInOrganizationAcl[];
  message: string;
}

export interface LinkedInProfile {
  sub: string; // LinkedIn member ID
  name: string;
  given_name: string;
  family_name: string;
  picture?: string;
  email: string;
  email_verified: boolean;
}

/**
 * Exchange LinkedIn authorization code for access token via backend API
 * Backend saves tokens securely and returns profile data
 * @param code - Authorization code from LinkedIn callback
 * @param customerId - Customer ID for the backend endpoint
 * @param idToken - User's authentication token for backend API
 * @param redirectUri - The exact redirect URI used in OAuth URL
 * @returns Token response with profile and organizations (no access_token - backend saves it)
 */
export const exchangeLinkedInToken = async (
  code: string,
  customerId: string,
  idToken: string,
  redirectUri: string
): Promise<LinkedInTokenResponse> => {
  debug.log("LinkedIn token exchange: start", {
    customerId,
    hasAuthCode: !!code,
    hasSessionToken: !!idToken,
  });

  if (!customerId) {
    throw new Error("Customer ID is required for LinkedIn token exchange");
  }
  
  if (!idToken) {
    throw new Error("User authentication token is required");
  }

  const apiUrl = `${SOCIAL_API_BASE}/${customerId}/linkedin/exchange-token`;
  const requestBody = {
    code,
    redirect_uri: redirectUri,
  };

  const startTime = Date.now();

  try {
    const response = await axios.post(
      apiUrl,
      requestBody,
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
      }
    );
    
    const duration = Date.now() - startTime;
    debug.log("LinkedIn token exchange: success", {
      ms: duration,
      status: response.status,
      profileId: response.data?.profile_id,
    });
    
    return response.data;
  } catch (error: any) {
    debug.error("LinkedIn token exchange failed:", formatSafeErrorMessage(error));
    
    if (error.response?.status === 400) {
      throw new Error(
        error.response?.data?.error || 
        error.response?.data?.message ||
        "Missing code or customer_id"
      );
    }
    
    if (error.response?.status === 401) {
      throw new Error(
        error.response?.data?.error || 
        error.response?.data?.message ||
        "Missing or invalid Cognito token"
      );
    }
    
    if (error.response?.status === 404) {
      throw new Error(
        "LinkedIn token exchange endpoint not found. Please verify the backend endpoint is deployed."
      );
    }
    
    if (error.response?.status === 500) {
      throw new Error(
        error.response?.data?.error || 
        error.response?.data?.message ||
        "LinkedIn client configuration incomplete"
      );
    }
    
    throw new Error(
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Failed to exchange LinkedIn authorization code"
    );
  }
};

/**
 * Exchange a LinkedIn *business* (Community Management / Sagelyn app) authorization code.
 * The backend holds the business token and returns the organizations the caller administers.
 * No personal profile is returned — the CMA app has no OIDC.
 */
export const exchangeLinkedInBusinessToken = async (
  code: string,
  customerId: string,
  idToken: string,
  redirectUri: string
): Promise<LinkedInBusinessTokenResponse> => {
  if (!customerId) {
    throw new Error("Customer ID is required for LinkedIn business token exchange");
  }
  if (!idToken) {
    throw new Error("User authentication token is required");
  }

  const apiUrl = `${SOCIAL_API_BASE}/${customerId}/linkedin/exchange-business-token`;
  try {
    const response = await axios.post(
      apiUrl,
      { code, redirect_uri: redirectUri },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    debug.error("LinkedIn business token exchange failed:", formatSafeErrorMessage(error));
    if (error.response?.status === 404) {
      throw new Error(
        "LinkedIn business token exchange endpoint not found. Please verify the backend endpoint is deployed."
      );
    }
    if (error.response?.status === 503) {
      throw new Error(
        error.response?.data?.error ||
        "LinkedIn business app is not configured. Please contact support."
      );
    }
    throw new Error(
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Failed to exchange LinkedIn business authorization code"
    );
  }
};

export const saveLinkedInOrganization = async (
  customerId: string,
  idToken: string,
  organizationUrn: string,
  personalProfileId: string,
  organizationName?: string,
  logoUrl?: string
): Promise<void> => {
  const apiUrl = `${SOCIAL_API_BASE}/${customerId}/linkedin/save-organization`;
  await axios.post(
    apiUrl,
    {
      organization_urn: organizationUrn,
      personal_profile_id: personalProfileId,
      organization_name: organizationName ?? '',
      logo_url: logoUrl ?? '',
    },
    {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${idToken}`,
      },
    }
  );
};

// --- Google Search Console ---

/**
 * Exchange a Search Console authorization code. The backend keeps the refresh token
 * and returns the properties this grant can connect — nothing is connected until
 * saveGscProperties picks one, so this is safe to call and then abandon.
 */
export const exchangeGscToken = async (
  code: string,
  customerId: string,
  idToken: string,
  redirectUri: string
): Promise<GSCExchangeResponse> => {
  if (!customerId) {
    throw new Error("Customer ID is required for Search Console token exchange");
  }
  if (!idToken) {
    throw new Error("User authentication token is required");
  }

  const apiUrl = `${SOCIAL_API_BASE}/${customerId}/gsc/exchange-token`;
  try {
    const response = await axios.post(
      apiUrl,
      { code, redirect_uri: redirectUri },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    // A grant with no usable properties comes back 200 with an empty list.
    return { ...response.data, sites: response.data?.sites ?? [] };
  } catch (error: any) {
    debug.error("Search Console token exchange failed:", formatSafeErrorMessage(error));
    if (error.response?.status === 503) {
      throw new Error(
        error.response?.data?.error ||
        "Google Search Console is not configured. Please contact support."
      );
    }
    throw new Error(
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Failed to connect Google Search Console"
    );
  }
};

/**
 * Connect one or more properties using the grant held by exchangeGscToken. Accepts a
 * list so a single consent can connect several properties without re-prompting.
 */
export const saveGscProperties = async (
  customerId: string,
  siteUrls: string[],
  idToken: string
): Promise<GSCSavePropertyResponse> => {
  const apiUrl = `${SOCIAL_API_BASE}/${customerId}/gsc/save-property`;
  try {
    const response = await axios.post(
      apiUrl,
      { site_urls: siteUrls },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    debug.error("Search Console save property failed:", formatSafeErrorMessage(error));
    if (error.response?.status === 404) {
      // The holding secret is gone — the grant was already spent or never happened.
      throw new Error(
        "Your Google Search Console authorization has expired. Please connect again."
      );
    }
    throw new Error(
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Failed to connect the Search Console property"
    );
  }
};

// --- Bing Webmaster Tools (SGL-134) ---
// The owner's own Bing API key, not OAuth (Sourav, ai-agent-management PR #385):
// check the key and list its verified sites, then save the chosen sites with it.
// The key travels only in these request bodies. It is never logged (debug.error gets
// formatSafeErrorMessage, which prints only the status and the server's message) and
// never appears in an error we throw.

/** POST JSON to a social-management route with the caller's Cognito token. */
const postSiteConnection = async <T>(path: string, body: unknown, idToken: string): Promise<T> => {
  const response = await axios.post(`${SOCIAL_API_BASE}/${path}`, body, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
  });
  return response.data;
};

/** The backend's own message when it sent one, else the transport's, else `fallback`. */
const siteConnectionErrorText = (error: any, fallback: string): string =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.message ||
  fallback;

/** Owner-facing words for social-management's Bing error codes (token_manager/app.py). */
const BING_ERROR_TEXT: Record<string, string> = {
  api_key_required: "Paste your Bing API key.",
  key_invalid:
    "That key didn't work. If you just generated it, Bing can take up to 30 minutes to activate it.",
  bing_unavailable: "Bing isn't responding right now. Try again in a few minutes.",
  bing_error: "Bing Webmaster Tools didn't respond as expected. Please try again.",
  not_a_verified_site_for_this_key:
    "This key doesn't cover that site. Use the key from the Bing account where the site is verified.",
};

const bingErrorText = (error: any, fallback: string): string => {
  const code = error.response?.data?.error;
  if (typeof code === "string" && BING_ERROR_TEXT[code]) return BING_ERROR_TEXT[code];
  if (error.response?.status === 503) {
    return "Bing Webmaster Tools isn't configured yet. Please contact support.";
  }
  if (error.response?.status >= 500 && !code) {
    // A gateway 5xx carries no reason of ours; axios's "status code 502" means nothing.
    return fallback;
  }
  return siteConnectionErrorText(error, fallback);
};

/**
 * Check a Bing Webmaster API key and list the verified sites it covers (SGL-134).
 * Nothing is stored: saveBingSites checks the key again and stores it.
 *
 * Every failure comes back as 400 or 502, never 401/403, which the axios
 * interceptor would treat as a sign-out.
 */
export const exchangeBingToken = async (
  apiKey: string,
  customerId: string,
  idToken: string
): Promise<BingExchangeResponse> => {
  if (!customerId) {
    throw new Error("Customer ID is required to connect Bing Webmaster Tools");
  }
  if (!idToken) {
    throw new Error("User authentication token is required");
  }
  const key = apiKey.trim();
  if (!key) {
    throw new Error(BING_ERROR_TEXT.api_key_required);
  }

  try {
    const data = await postSiteConnection<BingExchangeResponse>(
      `${customerId}/bing/exchange-token`,
      { api_key: key },
      idToken
    );
    // A key whose account has no verified site comes back 200 with an empty list.
    return { ...data, sites: data?.sites ?? [] };
  } catch (error: any) {
    debug.error("Bing Webmaster key check failed:", formatSafeErrorMessage(error));
    throw new Error(bingErrorText(error, "We couldn't check your Bing key. Please try again."));
  }
};

/**
 * Connect Bing sites with a key (SGL-134). Also the "Update key" path: saving the
 * connected sites again with a new key replaces the stored one and restarts its
 * 90-day reminder. The backend refuses a site the key doesn't list.
 */
export const saveBingSites = async (
  customerId: string,
  siteUrls: string[],
  idToken: string,
  apiKey: string
): Promise<BingSaveSiteResponse> => {
  const key = apiKey.trim();
  if (!key) {
    throw new Error(BING_ERROR_TEXT.api_key_required);
  }
  try {
    return await postSiteConnection<BingSaveSiteResponse>(
      `${customerId}/bing/save-site`,
      { api_key: key, site_urls: siteUrls },
      idToken
    );
  } catch (error: any) {
    debug.error("Bing Webmaster save site failed:", formatSafeErrorMessage(error));
    throw new Error(bingErrorText(error, "We couldn't save your Bing connection. Please try again."));
  }
};

// --- Google Drive: chat attachments ---

// Drive endpoints live in content-management (REACT_APP_CONTENT_API_URL already includes /content)
const CONTENT_API_BASE = process.env.REACT_APP_CONTENT_API_URL || "";

export interface GoogleDriveExchangeResponse {
  success?: boolean;
  message?: string;
  email?: string;
  profile_id?: string;
}

export const exchangeGoogleDriveToken = async (
  code: string,
  customerId: string,
  idToken: string,
  redirectUri: string
): Promise<GoogleDriveExchangeResponse> => {
  const apiUrl = `${CONTENT_API_BASE}/${customerId}/google-drive/exchange-token`;
  try {
    const response = await axios.post(
      apiUrl,
      { code, redirect_uri: redirectUri },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    debug.error("Google Drive token exchange failed:", formatSafeErrorMessage(error));
    throw new Error(
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Failed to connect Google Drive"
    );
  }
};

/**
 * Short-lived access token for Google Picker (server uses the stored refresh token).
 * Returns `null` when the caller should (re)start the OAuth flow: no stored Drive
 * connection (HTTP 404), or the stored refresh token was revoked/expired or was
 * issued by a different OAuth client (Google's invalid_grant).
 *
 * Pass `profileId` (the Google `sub` from the exchange) so the server resolves the
 * stored secret by exact name; without it the server must list secrets, which its
 * IAM role isn't permitted to do.
 */
export const getGoogleDrivePickerAccessToken = async (
  customerId: string,
  idToken: string,
  profileId?: string
): Promise<{ access_token: string } | null> => {
  const base = `${CONTENT_API_BASE}/${customerId}/google-drive/access-token`;
  const apiUrl = profileId
    ? `${base}?profile_id=${encodeURIComponent(profileId)}`
    : base;
  try {
    const response = await axios.get(apiUrl, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!response.data?.access_token) {
      throw new Error("Google Drive access token not returned by server");
    }
    return { access_token: response.data.access_token };
  } catch (error: any) {
    const backendError = error.response?.data?.error;
    if (
      error.response?.status === 404 ||
      backendError === "invalid_grant" // stored token revoked/expired → reconnect
    ) {
      return null;
    }
    debug.error("Failed to get Google Drive access token:", formatSafeErrorMessage(error));
    throw new Error(
      backendError ||
      error.message ||
      "Failed to get Google Drive access token"
    );
  }
};